import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Upload, X, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { currencySymbol, formatCurrency } from "@/lib/currency";
import { Field } from "./shared";

export const BidderView = ({ a, onChange }) => {
  const [form, setForm] = useState({
    price: a.my_current_round_bid?.price || "",
    delivery_timeline: a.my_current_round_bid?.delivery_timeline || "",
    payment_terms: a.my_current_round_bid?.payment_terms || "",
    warranty: a.my_current_round_bid?.warranty || "",
    remarks: a.my_current_round_bid?.remarks || "",
  });
  const [att, setAtt] = useState(a.my_current_round_bid ? {
    storage_path: a.my_current_round_bid.attachment_path,
    original_filename: a.my_current_round_bid.attachment_filename,
  } : null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAtt(data);
      toast.success("Attached");
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/auctions/${a.id}/bids`, {
        price: Number(form.price),
        delivery_timeline: form.delivery_timeline,
        payment_terms: form.payment_terms,
        warranty: form.warranty,
        remarks: form.remarks,
        attachment_path: att?.storage_path || null,
        attachment_filename: att?.original_filename || null,
      });
      toast.success("Bid submitted");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="bidder-view">
      <div className="lg:col-span-2 bg-[#F8F9FA] border border-black/10 p-6 space-y-5">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2">Current Round</div>
          <div className="font-display font-extrabold text-5xl tracking-tightest leading-none">R{a.current_round}</div>
        </div>
        {a.current_round > 1 && a.rounds?.[a.current_round - 2]?.target_price != null && (
          <div className="border-t border-black/10 pt-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">Creator&apos;s Target Price</div>
            <div className="font-mono-plex text-3xl klein-blue" data-testid="target-price-display">
              {formatCurrency(a.rounds[a.current_round - 2].target_price, a.currency)}
            </div>
            <p className="text-xs text-neutral-600 mt-2">Quote at or below this target to stay competitive.</p>
          </div>
        )}
        {a.my_current_round_bid && (
          <div className="border-t border-black/10 pt-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">Your Current Bid</div>
            <div className="font-mono-plex text-2xl" data-testid="my-current-bid">{formatCurrency(a.my_current_round_bid.price, a.currency)}</div>
            <div className="text-xs text-neutral-500 mt-1">You can update it any time during this round.</div>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="lg:col-span-3 border border-black/10 p-6 space-y-5" data-testid="bid-form">
        <div className="font-display font-extrabold text-2xl tracking-tightest">Submit Your Quote</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label={`Price (${currencySymbol(a?.currency)})`} required>
            <Input type="number" step="0.01" value={form.price} onChange={change("price")} required className="rounded-none border-black/20 h-11 font-mono-plex" data-testid="bid-price" />
          </Field>
          <Field label="Delivery Timeline" required>
            <Input value={form.delivery_timeline} onChange={change("delivery_timeline")} required placeholder="e.g. 30 days" className="rounded-none border-black/20 h-11" data-testid="bid-delivery" />
          </Field>
          <Field label="Payment Terms" required>
            <Input value={form.payment_terms} onChange={change("payment_terms")} required placeholder="e.g. 50% advance, 50% on delivery" className="rounded-none border-black/20 h-11" data-testid="bid-payment" />
          </Field>
          <Field label="Warranty / Validity" required>
            <Input value={form.warranty} onChange={change("warranty")} required placeholder="e.g. 2 years" className="rounded-none border-black/20 h-11" data-testid="bid-warranty" />
          </Field>
        </div>
        <Field label="Remarks">
          <Textarea value={form.remarks} onChange={change("remarks")} rows={3} className="rounded-none border-black/20" data-testid="bid-remarks" />
        </Field>
        <Field label="Attachment (optional)">
          {!att ? (
            <label className="border border-dashed border-black/20 p-4 flex items-center gap-3 cursor-pointer hover:border-black" data-testid="bid-upload-zone">
              <Upload size={18} weight="bold" />
              <span className="text-sm">{uploading ? "Uploading..." : "Click to upload"}</span>
              <input type="file" hidden onChange={onUpload} disabled={uploading} data-testid="bid-upload-input" />
            </label>
          ) : (
            <div className="border border-black/15 p-3 flex items-center gap-3" data-testid="bid-attachment">
              <FileText size={18} weight="bold" />
              <div className="flex-1 text-sm truncate">{att.original_filename}</div>
              <button type="button" onClick={() => setAtt(null)}><X size={14} weight="bold" /></button>
            </div>
          )}
        </Field>
        <Button type="submit" disabled={busy} className="w-full rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-12 btn-sharp" data-testid="submit-bid">
          {busy ? "Submitting..." : (a.my_current_round_bid ? "Update Bid" : "Submit Bid")}
          <ArrowRight size={14} weight="bold" className="ml-2" />
        </Button>
        <p className="text-xs text-neutral-500 leading-relaxed">
          <strong>Tip:</strong> if multiple bidders quote the exact same lowest price, the bid <em>submitted first</em> wins the tie. Submit early.
        </p>
      </form>
    </div>
  );
};
